import React, {
  useId, useLayoutEffect, useRef, useState,
} from 'react';
import Field from '../Field';
import NotePreview from '../calendar/attendance/NotePreview';
import ImagePicker from '../resources/ImagePicker';
import MessagePlaceholderMenu from './MessagePlaceholderMenu';
import { useFilesIfProvided } from '../../data/useFiles';
import { composeLimits } from '../../domain/notifications';
import { composeFormats, composeWord, previewPlaceholders } from '../../domain/compose';
import { resourceMessage } from '../../domain/resources';
import s from './drawers.module.css';
import c from './composer.module.css';

// MMS's message editor, as ours keeps it (#596, #604, #531): Markdown in a
// plain box, with a toolbar whose buttons write the Markdown around the
// selection, the placeholder menu, Insert Image from the studio's library,
// and Preview — the renderer the email uses, each placeholder shown as
// `<< its words >>`. core/src/compose.rs says what of MMS's each keeps.
//
// Two boxes are this editor, as in MMS: New Message's (NewMessageDrawer.jsx,
// which puts its Templates menu in `tools`) and a Custom Email Template's
// (#608, CustomTemplateDrawer.jsx). The text is the owner's (`value`,
// `onChange`); `edits` are useMessageBodyEdits.js's, which answer each
// button with the selection to put back.
//
// **The box keeps the focus through every edit.** A toolbar button, a
// The box keeps the focus through every edit. A toolbar button, a placeholder
// picked from its menu and a picture picked from the library all put the focus
// back in the box with the selection the core answered — the cursor right
// after a token or a picture — so the teacher keeps typing. The selection is
// put back in a layout effect, in the commit that writes the new text (a tab
// in the background is given no animation frames), and the menu's entries,
// like the toolbar's buttons, keep the focus from leaving at all
// (`onMouseDown`).
// toolbar's buttons, keep the focus from leaving at all (`onMouseDown`).
//
// Insert Image needs the library; an editor mounted without it (a test)
// offers no picker rather than a broken one, as the lesson-note editor does.

// The button's look: bold and italic say what they do in their own type.
const TOOL_CLASS = { bold: c.toolBold, italic: c.toolItalic };

// A mouse press on a control that must not take the box's focus.
const keepFocus = (e) => e.preventDefault();

// The Send Invoice drawer (#622) is a third: its placeholder menu has the
// invoice's tokens (`placeholders`) and its Preview fills them for the
// recipient on To (`previewText`) rather than naming them.
export default function MessageBodyEditor({
  label, value, onChange, edits, notice = null, error = null, required = false, tools = null,
  placeholders = null, previewText = previewPlaceholders,
}) {
  const limits = composeLimits();
  const formats = composeFormats();
  const library = useFilesIfProvided();
  const box = useRef(null);
  const bodyId = useId();
  const previewId = useId();
  const [preview, setPreview] = useState(false);
  const [picking, setPicking] = useState(false);
  // The selection to put back, set in the same event as the owner's new
  // text, so both land in one commit and the effect below sees the new text.
  const [restore, setRestore] = useState(null);

  useLayoutEffect(() => {
    if (!restore) return;
    const el = box.current;
    if (el) {
      el.focus();
      el.setSelectionRange(restore.start, restore.end);
    }
    setRestore(null);
  }, [restore]);

  const select = (at) => {
    if (at) setRestore({ start: at.start, end: at.end });
  };
  const selection = () => {
    const el = box.current;
    return el ? [el.selectionStart, el.selectionEnd] : [undefined, undefined];
  };

  return (
    <>
      {/* The label names the box by `htmlFor`: the toolbar sits between
          them, as MMS's sits on top of its editor. */}
      <Field label={label} htmlFor={bodyId} required={required} error={error}>
        <>
          <div className={c.toolbar} role="toolbar" aria-label={composeWord('toolbar')}>
            {formats.map((f) => (
              <button
                key={f.format}
                type="button"
                className={TOOL_CLASS[f.format] || c.tool}
                aria-label={f.label}
                title={f.label}
                onMouseDown={keepFocus}
                onClick={() => select(edits.format(f.format, ...selection()))}
              >
                {f.glyph}
              </button>
            ))}
            <MessagePlaceholderMenu
              placeholders={placeholders}
              onPick={(key) => select(edits.insertPlaceholder(key, ...selection()))}
            />
            <span className={c.toolGap} />
            {library ? (
              <button type="button" className={c.tool} onClick={() => setPicking(true)}>
                {resourceMessage('insert_image')}
              </button>
            ) : null}
            <button
              type="button"
              className={c.tool}
              aria-expanded={preview}
              aria-controls={previewId}
              onClick={() => setPreview((p) => !p)}
            >
              {composeWord('preview')}
            </button>
            {tools}
          </div>
          <textarea
            id={bodyId}
            ref={box}
            rows={10}
            className={s.input}
            value={value}
            maxLength={limits.body}
            onChange={(e) => onChange(e.target.value)}
          />
        </>
      </Field>
      <div className={c.hint}>{composeWord('format_hint')}</div>
      {notice ? <div className={c.notice} role="status">{notice}</div> : null}
      {preview ? (
        <NotePreview
          id={previewId}
          source={previewText(value)}
          label={label}
          caption={label}
          emptyText={composeWord('preview_empty')}
        />
      ) : null}
      {library ? (
        <ImagePicker
          open={picking}
          onClose={() => setPicking(false)}
          // The picker is open, so the box has lost its focus — but a
          // textarea keeps its selection when it does, which is where the
          // picture goes.
          onPick={(row) => select(edits.insertImage(row, ...selection()))}
        />
      ) : null}
    </>
  );
}
